/**
 * Two small canvas widgets:
 *   ScoreBoard — team score + match timer, sits under the minimap.
 *   GearBlock  — stance, health, equipment and field-upgrade charge, bottom left.
 */
import { COL, rgba, clamp01, fitCanvas, clearCanvas, devicePx, crispRect, chamferPath } from './Theme.js';
import { drawGlyphs, label, icon, ICON } from './Glyphs.js';

/* --------------------------------------------------------------- score */

export const SCORE_W = 208, SCORE_H = 66;

export class ScoreBoard {
  constructor(root) {
    this.cv = document.createElement('canvas');
    this.cv.className = 'hud-c hud-score';
    root.appendChild(this.cv);
    this.g = fitCanvas(this.cv, SCORE_W, SCORE_H);
  }
  resize(dpr = devicePx()) { this.g = fitCanvas(this.cv, SCORE_W, SCORE_H, dpr); this._key = null; }

  draw(s) {
    // Nothing here moves between whole seconds — skip the redraw entirely.
    const key = `${s.scoreA}|${s.scoreB}|${s.scoreLimit}|${Math.floor(s.matchTime)}`;
    if (key === this._key) return;
    this._key = key;
    const g = this.g;
    clearCanvas(g);
    const W = SCORE_W;

    // timer chip
    const mm = Math.floor(s.matchTime / 60);
    const ss = Math.floor(s.matchTime % 60);
    const t = `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    chamferPath(g, W / 2 - 39, 0, 78, 22, 5);
    g.fillStyle = 'rgba(4,7,10,0.72)';
    g.fill();
    drawGlyphs(g, t, W / 2, 4, 14, {
      align: 'center', color: s.matchTime < 60 ? COL.enemy : COL.white, weight: 0.17, tracking: 0.10,
    });

    // score row
    const y = 32;
    const limit = Math.max(1, s.scoreLimit);
    const a = clamp01(s.scoreA / limit), b = clamp01(s.scoreB / limit);

    drawGlyphs(g, String(s.scoreA), 4, y, 22, { color: COL.ally, weight: 0.17, tracking: 0.08 });
    drawGlyphs(g, String(s.scoreB), W - 4, y, 22, { align: 'right', color: COL.enemy, weight: 0.17, tracking: 0.08 });
    label(g, 'ALLIES', 4, y + 26, { size: 9, weight: 700, tracking: 0.22, color: rgba(COL.dim, 0.9) });
    label(g, 'AXIS', W - 4, y + 26, { size: 9, weight: 700, tracking: 0.22, align: 'right', color: rgba(COL.dim, 0.9) });

    // opposing progress bars meeting in the middle
    const barY = y + 8, barW = 58;
    g.fillStyle = rgba('#000000', 0.5);
    crispRect(g, W / 2 - barW - 2, barY - 1, barW * 2 + 4, 6);
    g.fillStyle = rgba(COL.white, 0.10);
    crispRect(g, W / 2 - barW, barY, barW * 2, 4);
    g.fillStyle = COL.ally;
    crispRect(g, W / 2 - barW * a, barY, barW * a, 4);
    g.fillStyle = COL.enemy;
    crispRect(g, W / 2, barY, barW * b, 4);
    g.fillStyle = rgba(COL.white, 0.5);
    crispRect(g, W / 2 - 0.5, barY - 2, 1, 8);
  }
}

/* ---------------------------------------------------------------- gear */

export const GEAR_W = 300, GEAR_H = 112;

export class GearBlock {
  constructor(root) {
    this.cv = document.createElement('canvas');
    this.cv.className = 'hud-c hud-gear';
    root.appendChild(this.cv);
    this.g = fitCanvas(this.cv, GEAR_W, GEAR_H);
  }
  resize(dpr = devicePx()) { this.g = fitCanvas(this.cv, GEAR_W, GEAR_H, dpr); this._key = null; }

  draw(s) {
    const hpFrac = s.health / Math.max(1, s.maxHealth);
    // The CRITICAL flash is the only animated element, so only that state
    // forces a per-frame redraw.
    const anim = hpFrac < 0.35 ? Math.round(s.time * 30) : 0;
    const key = `${Math.round(s.health)}|${s.maxHealth}|${s.stance}|${s.stanceLabel}|`
      + `${s.lethal}|${s.tactical}|${Math.round(s.fieldCharge * 100)}|${anim}`;
    if (key === this._key) return;
    this._key = key;
    const g = this.g;
    clearCanvas(g);
    const H = GEAR_H;

    /* -------------------------------------------------------- health bar */
    const hp = clamp01(s.health / Math.max(1, s.maxHealth));
    const showHealth = hp < 0.999;
    if (showHealth) {
      const bw = 140, bh = 7, bx = 2, by = 6;
      g.fillStyle = rgba('#000000', 0.58);
      crispRect(g, bx - 2, by - 2, bw + 4, bh + 4);
      g.fillStyle = rgba(COL.white, 0.12);
      crispRect(g, bx, by, bw, bh);
      const col = hp < 0.25 ? COL.enemy : hp < 0.55 ? COL.amber : COL.good;
      g.fillStyle = col;
      crispRect(g, bx, by, bw * hp, bh);
      // segment ticks every 25 hp
      g.fillStyle = rgba('#000000', 0.55);
      for (let i = 1; i < 4; i++) crispRect(g, bx + (bw * i) / 4, by, 1, bh);
      drawGlyphs(g, String(Math.round(s.health)), bx + bw + 10, by - 3, 14, {
        color: col, weight: 0.18, tracking: 0.1,
      });
      if (hp < 0.35) {
        label(g, 'CRITICAL', bx + bw + 40, by - 1, {
          size: 10, weight: 700, tracking: 0.20,
          color: rgba(COL.enemy, 0.55 + 0.45 * Math.abs(Math.sin(s.time * 6))),
        });
      }
    }

    /* ------------------------------------------------------------ stance */
    const stanceY = H - 26;
    icon(ICON.stance, g, 2, stanceY, 20, 18, rgba(COL.white, 0.92), s.stance);
    label(g, s.stanceLabel, 28, stanceY + 4, {
      size: 11, weight: 700, tracking: 0.20, color: rgba(COL.bone, 0.85),
    });

    /* --------------------------------------------------------- equipment */
    const slotY = H - 46, SL = 40;
    let x = 112;
    const slot = (fn, count, key) => {
      chamferPath(g, x, slotY, SL, SL, 7);
      g.fillStyle = count > 0 ? 'rgba(6,9,12,0.70)' : 'rgba(6,9,12,0.30)';
      g.fill();
      g.lineWidth = 1;
      g.strokeStyle = rgba(COL.white, count > 0 ? 0.26 : 0.08);
      g.stroke();
      icon(fn, g, x + 9, slotY + 6, 22, 28, count > 0 ? COL.white : rgba(COL.faint, 1));
      if (count > 1) {
        drawGlyphs(g, String(count), x + SL - 4, slotY + SL - 15, 12, {
          align: 'right', color: COL.white, weight: 0.20, tracking: 0.1,
        });
      }
      label(g, key, x + 3, slotY + 2, {
        size: 9, weight: 700, tracking: 0.06, color: rgba(COL.dim, 0.9),
      });
      x += SL + 6;
    };
    slot(ICON.grenade, s.lethal, 'G');
    slot(ICON.stun, s.tactical, 'Q');

    /* ---------------------------------------------------- field upgrade */
    const cx = x + 20, cy = slotY + SL / 2, r = 19;
    const p = clamp01(s.fieldCharge);
    g.lineWidth = 5;
    g.strokeStyle = rgba('#000000', 0.55);
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke();
    g.lineWidth = 3;
    g.strokeStyle = rgba(COL.white, 0.14);
    g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.stroke();
    g.lineWidth = 3.5;
    g.strokeStyle = p >= 1 ? COL.amber : COL.ally;
    g.beginPath(); g.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * p); g.stroke();
    drawGlyphs(g, String(Math.round(p * 100)), cx, cy - 7, 14, {
      align: 'center', color: p >= 1 ? COL.amber : rgba(COL.bone, 0.95), weight: 0.19, tracking: 0.08,
    });
  }
}
